import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import InputField from '../inputField';
import CustomButton from '../customButton';
import CustomCheckbox from '../customCheckbox';
import Tooltip from '../tooltip';
import { objectHasProperty } from '../../../utility/helpers';
import fields from './fields';
import ConditionalTooltip from '../conditionalTooltip';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    maxWidth: 900,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 460,
  },
  field: {
    position: 'relative',
  },
  input: {
    marginBottom: spacing(6),
  },
  inputErrorBorder: {
    border: `1px solid ${primary.red2}`,
  },
  errorMessage: {
    position: 'absolute',
    bottom: 4,
  },
  numberInput: {
    maxWidth: 70,
  },
  save: {
    marginLeft: 'auto',
  },
  section: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing(3),
  },
  checkboxTextLabel: {
    flexShrink: 0,
    width: '100%',
    maxWidth: 'calc(100% - 24px)',
  },
  helpTooltip: {
    marginTop: spacing(1),
    marginLeft: spacing(2),
  },
  iconTooltip: {
    cursor: 'pointer',
  },
}));

const INPUT_TYPE_NUMBER = 'number';
export const ABSENCE_TRANSPARENCY = 'users_see_absence';
const ABSENCE_EMAIL_NOTIFICATIONS = 'absence_email_notifications';
const ABSENCE_DOUBLE_APPROVAL = 'absence_double_approval';
const RECRUITMENT_TRANSPARENCY = 'users_see_recruitment';
const LEARNING_TRANSPARENCY = 'users_see_courses';
const SUCCESSION_TRANSPARENCY = 'users_see_succession';
const IS_2FA_REQUIRED = 'is_2fa_required';

const OrganizationSettingsFormLayout = ({
  translations,
  values,
  errors,
  isValid,
  handleSubmit,
  handleChange,
  handleBlur,
  setFieldValue,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <form onSubmit={handleSubmit} className={classes.form}>
        <div>
          {fields.map(field => {
            const hasError = objectHasProperty(errors, field.name);
            const inputClasses = classNames(classes.input, {
              [classes.inputErrorBorder]: hasError,
              [classes.numberInput]: field.type === INPUT_TYPE_NUMBER,
            });
            return (
              <div key={field.name} className={classes.field}>
                <InputField
                  className={inputClasses}
                  customErrorClass={classes.errorMessage}
                  type={field.type}
                  name={field.name}
                  label={translations[field.label]}
                  value={values[field.name] || field.defaultValue}
                  inputProps={field.inputProps}
                  error={hasError}
                  errorMessage={errors[field.name]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                />
              </div>
            );
          })}
          <div className={classes.section}>
            <CustomCheckbox
              labelTextClass={classes.checkboxTextLabel}
              labelText={translations.absenceTracking.label}
              isChecked={values[ABSENCE_TRANSPARENCY]}
              onChange={() => {
                setFieldValue(
                  ABSENCE_TRANSPARENCY,
                  !values[ABSENCE_TRANSPARENCY]
                );
                if (values[ABSENCE_TRANSPARENCY]) {
                  setFieldValue(ABSENCE_EMAIL_NOTIFICATIONS, false);
                  setFieldValue(ABSENCE_DOUBLE_APPROVAL, false);
                }
              }}
              shouldTopAlign
            />
            <Tooltip
              className={classes.helpTooltip}
              text={translations.absenceTracking.description}
            />
          </div>
          <div className={classes.section}>
            <CustomCheckbox
              labelTextClass={classes.checkboxTextLabel}
              labelText={translations.absenceNotifications.label}
              isChecked={values[ABSENCE_EMAIL_NOTIFICATIONS]}
              disabled={!values[ABSENCE_TRANSPARENCY]}
              onChange={() => {
                setFieldValue(
                  ABSENCE_EMAIL_NOTIFICATIONS,
                  !values[ABSENCE_EMAIL_NOTIFICATIONS]
                );
              }}
              shouldTopAlign
            />
            <Tooltip
              className={classes.helpTooltip}
              text={translations.absenceNotifications.description}
            />
          </div>
          <div className={classes.section}>
            <ConditionalTooltip
              className={classes.iconTooltip}
              message={
                translations.absenceTrackingDoubleApprove.disabledTooltip
              }
              addTooltip={!values[ABSENCE_TRANSPARENCY]}
            >
              <CustomCheckbox
                labelTextClass={classes.checkboxTextLabel}
                labelText={translations.absenceTrackingDoubleApprove.label}
                disabled={!values[ABSENCE_TRANSPARENCY]}
                isChecked={values[ABSENCE_DOUBLE_APPROVAL]}
                onChange={() => {
                  setFieldValue(
                    ABSENCE_DOUBLE_APPROVAL,
                    !values[ABSENCE_DOUBLE_APPROVAL]
                  );
                }}
                shouldTopAlign
              />
            </ConditionalTooltip>
            <Tooltip
              className={classes.helpTooltip}
              text={translations.absenceTrackingDoubleApprove.description}
            />
          </div>
          <div className={classes.section}>
            <CustomCheckbox
              labelTextClass={classes.checkboxTextLabel}
              labelText={translations.recruitment.label}
              isChecked={values[RECRUITMENT_TRANSPARENCY]}
              onChange={() => {
                setFieldValue(
                  RECRUITMENT_TRANSPARENCY,
                  !values[RECRUITMENT_TRANSPARENCY]
                );
              }}
              shouldTopAlign
            />
            <Tooltip
              className={classes.helpTooltip}
              text={translations.recruitment.description}
            />
          </div>
          <div className={classes.section}>
            <CustomCheckbox
              labelTextClass={classes.checkboxTextLabel}
              labelText={translations.learningHub.label}
              isChecked={values[LEARNING_TRANSPARENCY]}
              onChange={() => {
                setFieldValue(
                  LEARNING_TRANSPARENCY,
                  !values[LEARNING_TRANSPARENCY]
                );
              }}
              shouldTopAlign
            />
            <Tooltip
              className={classes.helpTooltip}
              text={translations.learningHub.description}
            />
          </div>
          <div className={classes.section}>
            <CustomCheckbox
              labelTextClass={classes.checkboxTextLabel}
              labelText={translations.succession.label}
              isChecked={values[SUCCESSION_TRANSPARENCY]}
              onChange={() => {
                setFieldValue(
                  SUCCESSION_TRANSPARENCY,
                  !values[SUCCESSION_TRANSPARENCY]
                );
              }}
              shouldTopAlign
            />
            <Tooltip
              className={classes.helpTooltip}
              text={translations.succession.description}
            />
          </div>
          <div className={classes.section}>
            <CustomCheckbox
              labelTextClass={classes.checkboxTextLabel}
              labelText={translations.twoFactorAuthentication.label}
              isChecked={values[IS_2FA_REQUIRED]}
              onChange={() => {
                setFieldValue(IS_2FA_REQUIRED, !values[IS_2FA_REQUIRED]);
              }}
              shouldTopAlign
            />
            <Tooltip
              className={classes.helpTooltip}
              text={translations.twoFactorAuthentication.description}
            />
          </div>
        </div>
        <CustomButton
          submit
          type="small"
          disabled={!isValid}
          className={classes.save}
        >
          {translations.submit}
        </CustomButton>
      </form>
    </div>
  );
};

OrganizationSettingsFormLayout.propTypes = {
  translations: PropTypes.object.isRequired,
};

export default OrganizationSettingsFormLayout;
